import React from 'react'
import type { SpinEvidence } from '../../../Generated/SpinEvidence.ts'
import { formatDate, parseDate } from '../../../Util/DateHandling.js'
import { Card } from '../AdminStatsPage/StatsUi.js'
import { InfoTip } from './SpinUi.js'
import { pct } from './spinRefDetail.logic.js'

const RDP_RANKS = ['Domain', 'Kingdom', 'Phylum', 'Class', 'Order', 'Family', 'Genus', 'Species']

const spark = (v: number | undefined): string => (v == null ? '—' : v.toFixed(3))

const SourceExtras: React.FC<{ evidence: SpinEvidence }> = ({ evidence: e }) => {
  if (e.source === 'BOLD') {
    return (
      <div className="space-y-0.5">
        <div className="break-all">
          Bin: {e.boldLink ? <span className="font-mono">{e.boldLink}</span> : '—'}
        </div>
        <div>Sharing: {e.binSharing == null ? '—' : e.binSharing ? 'yes' : 'no'}</div>
        <div>Countries: {e.binCountries || '—'}</div>
      </div>
    )
  }
  if (e.source === 'RDP') {
    return (
      <div>
        <span className="text-gray-500">
          Bootstrap supports
          <InfoTip tip="RDP bootstrap supports are estimates: they vary by about ±0.05 between runs on the same sequence and reference database." />
        </span>
        <div className="font-mono tabular-nums">
          {(e.rdpSupports ?? []).map((v, i) => (
            <span key={i} className="mr-2" title={RDP_RANKS[i]}>
              {v == null ? '—' : v.toFixed(2)}
            </span>
          ))}
        </div>
      </div>
    )
  }
  return <>—</>
}

const DbVersions: React.FC<{ versions: SpinEvidence['dbVersionSet'] }> = ({ versions }) => {
  const entries = Object.entries(versions ?? {})
  if (entries.length === 0) {
    return <span className="text-gray-400">versions unknown</span>
  }
  return (
    <div className="text-xs text-gray-500">
      {entries.map(([family, version]) => (
        <div key={family}>
          {family}: {version}
        </div>
      ))}
    </div>
  )
}

export const SpinEvidenceCard: React.FC<{ evidence: SpinEvidence[] | undefined }> = ({ evidence }) => {
  const rows = [...(evidence ?? [])].sort((a, b) => a.source.localeCompare(b.source))
  return (
    <Card title="Evidence">
      <p className="text-sm text-gray-600 mb-2">
        Point-in-time blast values: they move with every reference-database release without the identification changing.
        <InfoTip tip="Grade, e-value, sparks, BOLD bin details and RDP supports are as of the evaluation date and the reference database versions shown on each row." />
      </p>
      {rows.length === 0 ? (
        <p className="text-gray-500 text-sm">No blast evidence recorded for this SPIN.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-xs">
            <thead>
              <tr>
                <th>Source</th>
                <th className="text-right">Grade</th>
                <th className="text-right">E-value</th>
                <th className="text-right">Spark top</th>
                <th className="text-right">Spark alt</th>
                <th>Source details</th>
                <th>Evaluated at</th>
                <th>Reference DBs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.source} data-testid={`spin-evidence-${e.source}`} className="align-top">
                  <td>{e.source}</td>
                  <td className="text-right tabular-nums">{pct(e.gradePercentage)}</td>
                  <td className="text-right tabular-nums">{e.expectationValue == null ? '—' : e.expectationValue.toExponential(2)}</td>
                  <td className="text-right tabular-nums">{spark(e.sparkTop)}</td>
                  <td className="text-right tabular-nums">{spark(e.sparkAlt)}</td>
                  <td>
                    <SourceExtras evidence={e} />
                  </td>
                  <td>{formatDate(parseDate(e.evaluatedAt))}</td>
                  <td>
                    <DbVersions versions={e.dbVersionSet} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
